import { useMemo, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./styles.css";
import { mute, unmute } from "../../redux/slices/aiSlice";
import { contentTypeEnum } from "../../util/enums";
import NewsContent from "../Content/newsContent";
import TableContent from "../Content/tableContent";
import GraphContent from "../Content/graphContent";
import ListContent from "../Content/listContent";
import TextContent from "../Content/textContent";
import AIStatusIndicatorBar from "../../components/ui/AIStatusIndicator/AIStatusIndicatorMini";
import SimulationTable from "../Content/simulationTable";
import HistoricalRiskFactorTable from "../Content/historicalRiskFactorTable";
import RiskFactorForm from "../Content/riskFactorForm";

export default function ContentPage({
  contentType,
  chartData,
  onBack,
  sendToolResponse,
  setCurrentContentType,
  setOutputText,
}) {
  const [isLandscape, setIsLandscape] = useState(false);
  const dispatch = useDispatch();

  const ai = useSelector((s) => s.ai || s.aiSlice) || {};
  const iaStatus = ai.status || "idle";
  const muted = ai.muted || false;

  const ui = useSelector((s) => s.ui || s.uiSlice) || {};
  const chartTitle = ui.chartTitle || "";

  const IMAGE_BY_STATUS_SOUND = {
    loading: "/assets/onda-blanca1.png",
    listening: "/assets/onda-blanca1.png",
    speaking: "/assets/ondas-blancas.gif",
  };

  const IMAGE_BAR_BOTTOM = {
    loading: "/assets/barrainferior_static_dark.png",
    listening: "/assets/barra-inferior2.gif",
    speaking: "/assets/barrainferior_static.png",
  };

  const centerImageBar = useMemo(() => IMAGE_BAR_BOTTOM[iaStatus], [iaStatus]);

  const centerImageSource = useMemo(
    () => IMAGE_BY_STATUS_SOUND[iaStatus],
    [iaStatus]
  );

  const onPressMic = useCallback(() => {
    if (muted) {
      window.gptRealtimeControl?.continue?.();
      dispatch(unmute());
    } else {
      window.gptRealtimeControl?.stop?.();
      dispatch(mute());
    }
  }, [dispatch, muted]);

  const renderContent = () => {
    switch (contentType) {
      case contentTypeEnum.graph:
        return <GraphContent chartData={chartData} />;
      case contentTypeEnum.table:
        return <TableContent data={chartData} />;
      case contentTypeEnum.newsList:
        return <ListContent items={chartData} titleList="Related News" />;
      case contentTypeEnum.newsDetail:
        return <NewsContent data={chartData} />;
      case contentTypeEnum.text:
        return <TextContent data={chartData} />;
      case contentTypeEnum.simulationTable:
        return <SimulationTable resultContent={chartData} />;
      case contentTypeEnum.historicalRiskFactorDetail:
        return <HistoricalRiskFactorTable data={chartData} />;
      case contentTypeEnum.customRiskCalculation:
        return (
          <RiskFactorForm
            sendToolResponse={sendToolResponse}
            setCurrentContentType={setCurrentContentType}
            setOutputText={setOutputText}
          />
        );
      case contentTypeEnum.riskHistoricalList:
        return (
          <ListContent items={chartData} titleList="Historical Scenarios" />
        );
      default:
        return <div>There is no content to display</div>;
    }
  };

  useEffect(() => {
    const mm = window.matchMedia("(orientation: landscape)");
    const update = () => setIsLandscape(mm.matches);

    update();

    if (mm.addEventListener) mm.addEventListener("change", update);
    else mm.addListener(update);

    const onResize = () => update();
    window.addEventListener("orientationchange", update);
    window.addEventListener("resize", onResize);

    return () => {
      if (mm.removeEventListener) mm.removeEventListener("change", update);
      else mm.removeListener(update);
      window.removeEventListener("orientationchange", update);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <div className={`chart-bg ${isLandscape ? "is-landscape" : "is-portrait"}`}>
      <div className="ia-top-content">
        <button onClick={onBack} className="chart-topbtn" aria-label="Volver">
          <img src="/assets/back.png" alt="Atrás" className="chart-icon" />
        </button>
        <span className="ia-text-ryse" style={{ color: "#365d72" }}>
          {"RYSE"}
        </span>
        <button className="ia-btn" aria-label="ghost" />
      </div>

      <div className="page-content-wrap">
        <h2 className="chart-title">{chartTitle}</h2>
        {renderContent()}
      </div>

      <div
        className="chart-bottombar-bg"
        style={{ backgroundImage: `url(${centerImageBar})` }}
      >
        <div className="chart-bottombar">
          <AIStatusIndicatorBar
            status={iaStatus}
            muted={muted}
            onMicPress={onPressMic}
          />
        </div>
      </div>
    </div>
  );
}
